/**
 * Object cut-out via @huggingface/transformers + SlimSAM.
 * Built into the shared Transformers.js IIFE (see scripts/ai/transformersAi.js).
 */
import {
    SamModel,
    AutoProcessor,
    RawImage,
    env,
} from "@huggingface/transformers";

const MODEL_ID = "Xenova/slimsam-77-uniform";
const ORT_WASM_PATH = "/lib/onnxruntime-web-hf/";

configureTransformersEnv();

let modelLoaded = false;
let modelLoadPromise = null;
/** @type {Awaited<ReturnType<typeof SamModel.from_pretrained>> | null} */
let model = null;
/** @type {Awaited<ReturnType<typeof AutoProcessor.from_pretrained>> | null} */
let processor = null;

/** Cached vision-encoder output for the current working image. */
let imageEmbeddings = null;
let imagePositionalEmbeddings = null;
let embeddingWidth = 0;
let embeddingHeight = 0;
/** @type {number} cheap fingerprint so edits at the same size invalidate the cache */
let embeddingFingerprint = 0;
/** @type {import("@huggingface/transformers").RawImage | null} */
let embeddingRawImage = null;

function configureTransformersEnv() {
    if (!navigator.gpu) {
        console.warn("WebGPU is not supported by this browser. Object cut-out will use WASM.");
    }

    env.allowLocalModels = true;
    env.localModelPath = "/models/";
    env.allowRemoteModels = true;
    env.backends.onnx.wasm.wasmPaths = ORT_WASM_PATH;
    env.backends.onnx.wasm.numThreads = 1;
    env.backends.onnx.wasm.proxy = false;
}

function samModelReady() {
    return modelLoaded;
}

function clearSamImageEmbeddings() {
    imageEmbeddings = null;
    imagePositionalEmbeddings = null;
    embeddingWidth = 0;
    embeddingHeight = 0;
    embeddingFingerprint = 0;
    embeddingRawImage = null;
}

/** @param {ImageData} imageData */
function fingerprintImageData(imageData) {
    const d = imageData.data;
    const n = d.length;
    let h = (n ^ (imageData.width << 16) ^ imageData.height) >>> 0;
    if (n >= 16) {
        h = (h ^ d[0] ^ (d[1] << 8) ^ (d[2] << 16) ^ (d[3] << 24)) >>> 0;
        h = (h ^ d[n - 4] ^ (d[n - 3] << 8) ^ (d[n - 2] << 16) ^ (d[n - 1] << 24)) >>> 0;
        const mid = ((n >>> 3) << 2);
        h = (h ^ d[mid] ^ (d[mid + 1] << 8) ^ (d[mid + 2] << 16) ^ (d[mid + 3] << 24)) >>> 0;
    }
    return h;
}

async function loadSamModel() {
    if (modelLoaded) {
        return;
    }

    if (typeof beginFilterProcessingWait === "function") {
        await beginFilterProcessingWait(LOADING_AI_MODELS, LOADING_AI_MODELS_BODY, { immediate: true });
    }
    try {
        if (modelLoadPromise) {
            return await modelLoadPromise;
        }

        modelLoadPromise = loadSamModelImpl();
        try {
            await modelLoadPromise;
        } finally {
            modelLoadPromise = null;
        }
    } finally {
        if (typeof endFilterProcessingWait === "function") {
            endFilterProcessingWait();
        }
    }
}

async function loadSamModelImpl() {
    try {
        // SlimSAM's WebGPU paths are unreliable across adapters (notably its
        // quantized DequantizeLinear shader). q8 WASM is small and consistent.
        model = await SamModel.from_pretrained(MODEL_ID, {
            dtype: "q8",
            device: "wasm",
        });
    } catch (err) {
        const message = err?.message || String(err || "Failed to load SAM model.");
        alert(message);
        throw err || new Error(message);
    }

    processor = await AutoProcessor.from_pretrained(MODEL_ID);
    modelLoaded = true;
}

/**
 * Encode the working image once so subsequent clicks only run the mask decoder.
 * @param {ImageData} imageData
 */
async function prepareSamImageEmbeddings(imageData) {
    if (!modelLoaded || !model || !processor) {
        throw new Error("SAM model is not loaded.");
    }
    if (!imageData) {
        throw new Error("No image data for SAM embeddings.");
    }

    const fingerprint = fingerprintImageData(imageData);
    if (
        imageEmbeddings &&
        embeddingWidth === imageData.width &&
        embeddingHeight === imageData.height &&
        embeddingFingerprint === fingerprint
    ) {
        return;
    }

    const rawImage = new RawImage(imageData.data, imageData.width, imageData.height, 4);
    const inputs = await processor(rawImage, { input_points: [[[0, 0]]] });
    const embeddings = await model.get_image_embeddings(inputs);

    imageEmbeddings = embeddings.image_embeddings;
    imagePositionalEmbeddings = embeddings.image_positional_embeddings;
    embeddingWidth = imageData.width;
    embeddingHeight = imageData.height;
    embeddingFingerprint = fingerprint;
    embeddingRawImage = rawImage;
}

/**
 * @param {ImageData} imageData
 * @param {Array<{ x: number, y: number, label: number }>} points
 * @returns {Promise<ImageData|null>} cut-out ImageData (transparent outside mask)
 */
async function predictSamCutOut(imageData, points) {
    if (!modelLoaded || !model || !processor) {
        return null;
    }
    if (!imageData || !points?.length) {
        return null;
    }
    if (typeof copyImageData !== "function") {
        throw new Error("copyImageData is not available.");
    }

    return await predictSamCutOutImpl(imageData, points);
}

async function predictSamCutOutImpl(imageData, points) {
    await prepareSamImageEmbeddings(imageData);

    // Shape: [batch, point_batch, num_points, 2] / [batch, point_batch, num_points]
    const input_points = [[points.map((p) => [p.x, p.y])]];
    const input_labels = [[points.map((p) => (p.label === 0 ? 0 : 1))]];

    const inputs = await processor(embeddingRawImage, { input_points, input_labels });
    const outputs = await model({
        ...inputs,
        image_embeddings: imageEmbeddings,
        image_positional_embeddings: imagePositionalEmbeddings,
    });

    const masks = await processor.post_process_masks(
        outputs.pred_masks,
        inputs.original_sizes,
        inputs.reshaped_input_sizes
    );

    const maskTensor = masks?.[0];
    if (!maskTensor?.dims || !maskTensor.data) {
        return null;
    }

    const dims = maskTensor.dims;
    // Expected: [1, numMasks, H, W]
    const numMasks = dims.length === 4 ? dims[1] : dims[0];
    const height = dims.length === 4 ? dims[2] : dims[1];
    const width = dims.length === 4 ? dims[3] : dims[2];
    if (!numMasks || !width || !height) {
        return null;
    }

    const scores = outputs.iou_scores?.data;
    let best = 0;
    if (scores && scores.length >= numMasks) {
        for (let i = 1; i < numMasks; i++) {
            if (scores[i] > scores[best]) best = i;
        }
    }

    const plane = width * height;
    const offset = best * plane;
    const maskData = maskTensor.data;
    const out = copyImageData(imageData);
    const src = imageData.data;
    const dst = out.data;

    for (let i = 0; i < plane; i++) {
        // post_process_masks returns logits for SAM masks; a non-zero check would
        // incorrectly include every negative background value.
        const on = maskData[offset + i] > 0 ? 1 : 0;
        const pi = i * 4;
        dst[pi] = src[pi];
        dst[pi + 1] = src[pi + 1];
        dst[pi + 2] = src[pi + 2];
        dst[pi + 3] = on ? src[pi + 3] : 0;
    }

    return out;
}

/**
 * Propose distinct foreground objects by sampling locations across the image and
 * rejecting masks that are too small, canvas-sized, or duplicates.
 * SAM's image encoder is shared for every proposal, so only the cheap mask
 * decoder runs after the first candidate.
 *
 * @param {ImageData} imageData
 * @param {{ maxObjects?: number, gridColumns?: number, gridRows?: number }} [options]
 * @returns {Promise<ImageData[]>}
 */
async function detectSamObjects(imageData, options = {}) {
    if (!modelLoaded || !imageData?.data) {
        return [];
    }

    const maxObjects = Math.max(1, Math.min(12, options.maxObjects ?? 8));
    const columns = Math.max(3, options.gridColumns ?? 7);
    const rows = Math.max(3, options.gridRows ?? 5);
    const candidates = [];

    for (let row = 0; row < rows; row++) {
        for (let column = 0; column < columns; column++) {
            candidates.push({
                x: Math.round((column + 0.5) * imageData.width / columns),
                y: Math.round((row + 0.5) * imageData.height / rows),
            });
        }
    }

    // Center-first gives the most visually prominent subjects priority when the
    // image contains more distinct objects than the layer limit.
    candidates.sort((a, b) =>
        Math.hypot(a.x - imageData.width / 2, a.y - imageData.height / 2) -
        Math.hypot(b.x - imageData.width / 2, b.y - imageData.height / 2));

    const accepted = [];
    const totalPixels = imageData.width * imageData.height;
    for (const point of candidates) {
        const cutOut = await predictSamCutOut(imageData, [{ ...point, label: 1 }]);
        if (!cutOut) {
            continue;
        }

        const area = countOpaquePixels(cutOut);
        const coverage = area / totalPixels;
        // Skip noise and masks that are effectively the unseparated canvas.
        if (coverage < 0.004 || coverage > 0.82) {
            continue;
        }
        if (accepted.some((existing) => masksDescribeSameObject(existing, cutOut))) {
            continue;
        }

        accepted.push(cutOut);
        if (accepted.length >= maxObjects) {
            break;
        }
    }

    return accepted;
}

/** @param {ImageData} imageData */
function countOpaquePixels(imageData) {
    let count = 0;
    for (let i = 3; i < imageData.data.length; i += 4) {
        if (imageData.data[i] > 0) count++;
    }
    return count;
}

/**
 * Use IoU and containment: two nearby prompts frequently produce the same
 * subject with slightly different boundaries, which must remain one layer.
 */
function masksDescribeSameObject(a, b) {
    let intersection = 0;
    let aCount = 0;
    let bCount = 0;
    for (let i = 3; i < a.data.length; i += 4) {
        const aOn = a.data[i] > 0;
        const bOn = b.data[i] > 0;
        if (aOn) aCount++;
        if (bOn) bCount++;
        if (aOn && bOn) intersection++;
    }
    const union = aCount + bCount - intersection;
    const iou = union ? intersection / union : 0;
    const containment = intersection / Math.max(1, Math.min(aCount, bCount));
    return iou >= 0.72 || containment >= 0.9;
}

Object.assign(globalThis, {
    samModelReady,
    loadSamModel,
    clearSamImageEmbeddings,
    prepareSamImageEmbeddings,
    predictSamCutOut,
    detectSamObjects,
});
